import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import CustomKeyboardDateTimePicker from '../KeyboardDateTimePicker/KeyboardDateTimePicker';
import CustomButton from '../Button/Button';
import DataGrid from '../DataGrid/DataGrid';
import {PaginationMode} from '../DataGrid/types';
import {actions} from '../../store';
import {ReduxState} from '../../store/types';
import {
    generateTransactionColumns,
    useStyles as useTransactionStyles,
} from '../../views/Accounts/AccountDetails/tabs/TransactionsTab.utils';
import dayjs from '../../services/customDayJs';
import {TransactionItem} from '../../store/actions/accounts/payloads';
import {makeStyles} from '@material-ui/core/styles';
import {convertUserLocalTimeToUtc} from '../../utils/dateWithTimezoneConversion';

const dateFormat = 'YYYY-MM-DD HH:mm:ss';

const useStyles = makeStyles(() => ({
    filtersRow: {
        display: 'flex',
        alignItems: 'flex-start',

        '& .MuiFormControl-root': {
            marginRight: 18,
        },

        '& .date-time-picker': {
            '& .MuiInputBase-input': {
                width: 208,
            },
        },
    },
    searchButton: {
        minWidth: 96,
        marginTop: 8,
    },
}));

export interface TransactionsContentProps {
    iAccount: number;
    hideActions?: boolean;
    onClickDetails?: (item: TransactionItem) => void;
    resetKey?: unknown;
    filtersClassName?: string;
    tableClassName?: string;
    searchButtonClassName?: string;
}

const TransactionsContent: React.VFC<TransactionsContentProps> = ({
    iAccount,
    hideActions,
    onClickDetails,
    resetKey,
    filtersClassName,
    tableClassName,
    searchButtonClassName,
}) => {
    const classes = useStyles();
    const transactionClasses = useTransactionStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const initPageSize = 10;

    const timezoneOffset = useSelector<ReduxState, number>(
        (state) => state.generic.sessionData?.tz_offset || 0,
    );

    const defaultFrom = dayjs
        .utc()
        .utcOffset(timezoneOffset / 60)
        .subtract(1, 'month')
        .format(dateFormat);
    const defaultTo = dayjs
        .utc()
        .utcOffset(timezoneOffset / 60)
        .format(dateFormat);

    const [fromDate, setFromDate] = useState(defaultFrom);
    const [toDate, setToDate] = useState(defaultTo);
    const [forceFirstPage, setForceFirstPage] = useState(false);
    const [pageSize, setPageSize] = useState(initPageSize);

    const {transactions} = useSelector((state: ReduxState) => state.accounts);
    const userDateFormat = useSelector<ReduxState, string>(
        (state) =>
            state.generic.globalCustomerInfo?.customer_info?.out_date_format || '',
    );

    const userDateTimeFormat = useSelector<ReduxState, string>(
        (state) =>
            state.generic.globalCustomerInfo?.customer_info
                ?.out_date_time_format || '',
    );

    const customerCurrency = useSelector<ReduxState, string>(
        (state) =>
            state.calls.customerCurrency
            || state.generic.globalCustomerInfo?.customer_info?.iso_4217
            || '',
    );

    const fetchTransactions = (
        page = 0,
        limit = pageSize,
        overrideFrom?: string,
        overrideTo?: string,
    ) => {
        const from = overrideFrom ?? fromDate;
        const to = overrideTo ?? toDate;
        dispatch(
            actions.getAccountTransactions.request({
                i_account: iAccount,
                fromDate: convertUserLocalTimeToUtc(from, timezoneOffset),
                toDate: convertUserLocalTimeToUtc(to, timezoneOffset),
                limit,
                offset: page * limit,
            }),
        );
    };

    useEffect(() => {
        if (iAccount) {
            setFromDate(defaultFrom);
            setToDate(defaultTo);
            fetchTransactions(0, pageSize, defaultFrom, defaultTo);
        }
    }, [iAccount, resetKey]);

    const handleSearch = () => {
        setForceFirstPage(true);
        fetchTransactions();
    };

    const columns = useMemo(
        () => generateTransactionColumns(
            t,
            transactionClasses,
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onClickDetails ?? (() => {}),
            userDateFormat,
            customerCurrency,
            hideActions,
        ),
        [userDateFormat, customerCurrency, hideActions, onClickDetails],
    );

    return (
        <>
            <div className={filtersClassName ?? classes.filtersRow}>
                <CustomKeyboardDateTimePicker
                    label={t('screens:accounts.fromDate')}
                    value={fromDate}
                    onChange={(val) => setFromDate(val ?? defaultFrom)}
                    withHours
                    dataQa="transactions-from-date"
                    userDateTimeFormat={userDateTimeFormat}
                />
                <CustomKeyboardDateTimePicker
                    label={t('screens:accounts.toDate')}
                    value={toDate}
                    onChange={(val) => setToDate(val ?? defaultTo)}
                    withHours
                    dataQa="transactions-to-date"
                    userDateTimeFormat={userDateTimeFormat}
                />
                <CustomButton
                    primary
                    accent
                    dataQa="transactions-search"
                    onClick={handleSearch}
                    className={searchButtonClassName ?? classes.searchButton}
                >
                    {t('common:search')}
                </CustomButton>
            </div>

            <div className={tableClassName}>
                <DataGrid
                    columns={columns}
                    data={transactions.items}
                    rowCount={transactions.total}
                    loading={transactions.isLoading}
                    paginationMode={PaginationMode.Server}
                    onPageChange={({page, pageSize: size}) => {
                        setForceFirstPage(false);
                        setPageSize(size);
                        fetchTransactions(page, size);
                    }}
                    onPageSizeChange={({pageSize: size}) => {
                        setForceFirstPage(true);
                        setPageSize(size);
                        fetchTransactions(0, size);
                    }}
                    initialPageSize={initPageSize}
                    forceFirstPage={forceFirstPage}
                    showMessageWhenEmpty
                    getItemId={(row) => String(row.i_xdr)}
                />
            </div>
        </>
    );
};

export default TransactionsContent;